import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, Edit3, MoreHorizontal, Settings as SettingsIcon, Plus, ArrowUp, Mic, X,
  FileText, Link2, Volume2, AlertTriangle, Bookmark, Trash2, MessageSquare, Check, Copy,
  MessageCircle, BookOpen, Feather, Search, Users, Gift, ShieldAlert, Image as ImageIcon, Radio, ChevronLeft, Zap,
} from "lucide-react";
import { useAuth } from "@/auth";
import { apiFetch, getToken } from "@/api";
import { Button, Spinner, cn } from "@/ui";
import { TABS } from "@/constants";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { VoiceRecordingBar } from "@/components/VoiceRecordingBar";
import { VoiceSession } from "@/components/VoiceSession";
import { BottomNav } from "@/components/BottomNav";
import { ChatBackground, BackgroundPicker } from "@/backgrounds";
import { QuizPlayer } from "@/components/QuizPlayer";

const ICONS = { MessageCircle, BookOpen, Feather, Search };
const TITLES = { everyday: "Ask anything", learn: "Learn", create: "Create", research: "Research" };
const PLACEHOLDERS = { everyday: "Ask me anything…", learn: "What do you want to learn?", create: "What should we make?", research: "What do you want to look into?" };
const MODE_ICON = { everyday: MessageCircle, learn: BookOpen, create: Feather, research: Search };
const MODE_ACCENT = {
  everyday: { text: "text-ask", soft: "bg-ask/15", dot: "bg-ask" },
  learn: { text: "text-learn", soft: "bg-learn/15", dot: "bg-learn" },
  create: { text: "text-create", soft: "bg-create/15", dot: "bg-create" },
  research: { text: "text-research", soft: "bg-research/15", dot: "bg-research" },
};

function cleanText(t) {
  return (t || "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`{1,3}/g, "")
    .trim();
}

// When a reply is likely to trigger a live web lookup (Research tab, or a query that implies
// current/external info) we show a "Searching the web…" chip instead of the plain thinking state.
const SEARCH_HINT_RE = /\b(search|latest|current|today|recent|news|price|prices|weather|score|source|sources|cite|citation|verify|fact.?check|who won|when did|how much|stock|2024|2025|2026|this year|right now)\b/i;
function willSearch(tab, text) {
  return tab === "research" || SEARCH_HINT_RE.test(text || "");
}

// Renders MAXx's answer as rich markdown (bold, lists, links) and styles numbered citation
// markers like [1] — produced as [1](url) by the backend — as small superscript chips.
function ChatMarkdown({ text }) {
  return (
    <div data-testid="assistant-markdown" className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...p }) => <p className="mb-2 last:mb-0 leading-relaxed" {...p} />,
          ul: ({ node, ...p }) => <ul className="mb-2 list-disc space-y-1 pl-5" {...p} />,
          ol: ({ node, ...p }) => <ol className="mb-2 list-decimal space-y-1 pl-5" {...p} />,
          li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
          h1: ({ node, ...p }) => <p className="mb-1 mt-2 font-bold" {...p} />,
          h2: ({ node, ...p }) => <p className="mb-1 mt-2 font-bold" {...p} />,
          h3: ({ node, ...p }) => <p className="mb-1 mt-2 font-bold" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold" {...p} />,
          em: ({ node, ...p }) => <em className="italic" {...p} />,
          code: ({ node, inline, ...p }) =>
            inline
              ? <code className="rounded bg-foreground/10 px-1 py-0.5 text-[0.85em]" {...p} />
              : <code className="my-2 block overflow-x-auto rounded-lg bg-foreground/10 p-3 text-[0.85em]" {...p} />,
          a: ({ node, children, href, ...p }) => {
            const label = Array.isArray(children) ? children.join("") : String(children ?? "");
            const isCite = /^\[?\d+\]?$/.test(label.trim());
            return (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={isCite ? "citation-marker" : undefined}
                className={cn(
                  isCite
                    ? "mx-[1px] inline-flex h-[15px] min-w-[15px] translate-y-[-5px] items-center justify-center rounded-full bg-foreground/10 px-[3px] text-[9px] font-semibold leading-none text-muted-foreground no-underline transition-colors hover:bg-foreground/20 hover:text-foreground"
                    : "font-medium text-primary underline underline-offset-2",
                )}
                {...p}
              >
                {isCite ? label.replace(/[[\]]/g, "") : children}
              </a>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

export default function Chat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "everyday";
  const prefill = searchParams.get("q") || "";
  const accent = MODE_ACCENT[tab] || MODE_ACCENT.everyday;
  const ModeIcon = MODE_ICON[tab] || MessageCircle;
  const { account, booting, updateSettings } = useAuth();
  const [activeByTab, setActiveByTab] = useState({});
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recTimerRef = useRef(null);
  const [thankYou, setThankYou] = useState(null);
  const [modNotice, setModNotice] = useState(null);
  const [bgPicker, setBgPicker] = useState(false);
  const [bgMenu, setBgMenu] = useState(false);
  const [bgToken, setBgToken] = useState("");
  const [bgUploading, setBgUploading] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [quizSession, setQuizSession] = useState(null);
  const [quizModal, setQuizModal] = useState(false);
  const [quizTopic, setQuizTopic] = useState("");
  const [quizBusy, setQuizBusy] = useState(false);
  const [quizCount, setQuizCount] = useState(5);
  const scrollRef = useRef(null);
  const scrollModeRef = useRef("bottom");
  const pinIdRef = useRef(null);
  const fileImgRef = useRef(null);
  const fileDocRef = useRef(null);
  const composerRef = useRef(null);
  const mediaRef = useRef(null);
  const cancelRef = useRef(false);
  const lastWasVoice = useRef(false);

  useEffect(() => {
    if (!booting && !account) navigate("/onboarding");
  }, [booting, account]);

  useEffect(() => {
    (async () => {
      try { const d = await apiFetch("/user/sponsor-thank-you"); if (d.thank_you) setThankYou(d.thank_you); } catch {}
      try { const d = await apiFetch("/user/moderation-notice"); if (d.notice) setModNotice(d.notice); } catch {}
      try { const d = await apiFetch("/user/background-token"); if (d.token) setBgToken(d.token); } catch {}
    })();
  }, []);

  const bg = account?.settings?.chat_backgrounds?.[tab] || null;
  const hasBg = !!bg && bg.type !== "none";
  const bgImageUrl = bg?.type === "image" && bgToken
    ? `${process.env.REACT_APP_BACKEND_URL}/api/user/background/file/${bg.value}?t=${bgToken}`
    : null;

  const saveBg = async (value) => {
    try { await updateSettings({ chat_background: { tab, value } }); }
    catch (e) { toast(e.message); }
  };
  const selectPreset = async (id) => { await saveBg({ type: "preset", value: id }); setBgPicker(false); };
  const clearBg = async () => { await saveBg(null); setBgPicker(false); };
  const uploadBg = async (file) => {
    if (file.size > 6 * 1024 * 1024) { toast("Image too large (max 6MB)."); return; }
    setBgUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const d = await apiFetch("/user/background/upload", { method: "POST", form });
      if (d.token) setBgToken(d.token);
      await saveBg({ type: "image", value: d.storage_path });
      setBgPicker(false);
    } catch (e) { toast(e.message || "Upload failed."); }
    finally { setBgUploading(false); }
  };

  const dismissModNotice = async () => {
    setModNotice(null);
    try { await apiFetch("/user/moderation-notice/ack", { method: "POST" }); } catch {}
  };

  const dismissThankYou = async () => {
    setThankYou(null);
    try { await apiFetch("/user/sponsor-thank-you/ack", { method: "POST" }); } catch {}
  };

  const loadChat = useCallback(async (id) => {
    setLoading(true);
    scrollModeRef.current = "bottom";
    try {
      if (!id) {
        setMessages([]);
        setCurrentChatId(null);
      } else {
        const d = await apiFetch(`/user/chats/${id}/messages`);
        setMessages(d.messages || []);
        setCurrentChatId(id);
      }
    } catch {
      setMessages([]);
      setCurrentChatId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const resolveTab = useCallback(async () => {
    try {
      const ent = await apiFetch("/user/entitlement");
      if (!ent.entitled) {
        navigate("/paywall");
        return;
      }
    } catch {}
    const activeId = activeByTab[tab];
    if (activeId === undefined) {
      setLoading(true);
      try {
        const d = await apiFetch(`/user/chats?tab=${tab}`);
        const chats = (d.chats || []).slice().sort((a, b) => (b.last_activity_at || "").localeCompare(a.last_activity_at || ""));
        await loadChat(chats.length ? chats[0].id : null);
      } catch {
        await loadChat(null);
      }
    } else {
      await loadChat(activeId);
    }
  }, [tab, activeByTab, loadChat, navigate]);

  const autoSendRef = useRef("");
  useEffect(() => {
    // Skip loading the latest chat on the initial render when auto-sending a "Try something" prompt.
    if (prefill && autoSendRef.current !== prefill + "|" + tab) return;
    resolveTab();
    // eslint-disable-next-line
  }, [tab, activeByTab[tab]]);

  useEffect(() => {
    if (!prefill) return;
    const key = prefill + "|" + tab;
    if (autoSendRef.current === key) return;
    autoSendRef.current = key;
    setLoading(false);
    setMessages([]);
    setCurrentChatId(null);
    doSend(prefill);
    // eslint-disable-next-line
  }, [prefill, tab]);

  const scrollEnd = () => setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 60);
  const pinTop = () => setTimeout(() => {
    const c = scrollRef.current;
    const el = pinIdRef.current && document.getElementById("m-" + pinIdRef.current);
    if (!c || !el) return;
    c.scrollTop += el.getBoundingClientRect().top - c.getBoundingClientRect().top - 12;
  }, 60);
  useEffect(() => { if (scrollModeRef.current === "top") pinTop(); else scrollEnd(); }, [messages]);

  const setActive = (id) => setActiveByTab((m) => ({ ...m, [tab]: id }));

  const doSend = async (override, viaVoice = false) => {
    const bodyText = (override ?? text).trim();
    if ((!bodyText && attachments.length === 0) || sending) return;
    const tmpU = `u-${Date.now()}`;
    const tmpA = `a-${Date.now()}`;
    pinIdRef.current = tmpU;
    scrollModeRef.current = "top";
    const local = attachments.map((a) => ({ type: a.type, name: a.name, url: a.url }));
    lastWasVoice.current = viaVoice;
    setMessages((p) => [...p, { id: tmpU, role: "user", text: bodyText, attachments: local }, { id: tmpA, role: "assistant", _typing: true, _searching: willSearch(tab, bodyText) }]);
    setText("");
    const sent = attachments.map((a) => ({ type: a.type, name: a.name, data_base64: a.base64, text: a.text }));
    setAttachments([]);
    setSending(true);
    pinTop();
    try {
      const data = await apiFetch("/user/message", { method: "POST", body: { tab, chat_id: currentChatId, text: bodyText, attachments: sent } });
      setCurrentChatId(data.chat.id);
      setActive(data.chat.id);
      setMessages((p) => p.filter((m) => m.id !== tmpA).concat([data.message]));
      if (viaVoice && data.message?.text) playTTS(data.message.text);
    } catch (e) {
      if (e.status === 402) { navigate("/paywall"); return; }
      setMessages((p) => p.filter((m) => m.id !== tmpA).map((m) => (m.id === tmpU ? { ...m, _error: true, _retry: bodyText } : m)));
    } finally {
      setSending(false);
    }
  };

  const submitEdit = async () => {
    const newText = editDraft.trim();
    const mid = editingId;
    if (!newText || sending || !mid) return;
    setEditingId(null);
    setEditDraft("");
    setSending(true);
    scrollModeRef.current = "top";
    const tmpA = `a-${Date.now()}`;
    setMessages((p) => {
      const idx = p.findIndex((m) => m.id === mid);
      if (idx === -1) return p;
      return [...p.slice(0, idx), { ...p[idx], text: newText, _error: false }, { id: tmpA, role: "assistant", _typing: true }];
    });
    pinTop();
    try {
      const data = await apiFetch("/user/message/edit", { method: "POST", body: { tab, chat_id: currentChatId, message_id: mid, text: newText } });
      setMessages((p) => p.filter((m) => m.id !== tmpA).concat([data.message]));
    } catch (e) {
      if (e.status === 402) { navigate("/paywall"); return; }
      toast(e.message || "Couldn't update the message.");
      if (currentChatId) { try { const d = await apiFetch(`/user/chats/${currentChatId}/messages`); setMessages(d.messages || []); } catch {} }
    } finally {
      setSending(false);
    }
  };
  const startEdit = (m) => { setEditingId(m.id); setEditDraft(m.text || ""); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(""); };
  const copyMsg = async (t) => { try { await navigator.clipboard.writeText(t || ""); toast("Copied"); } catch { toast("Couldn't copy"); } };

  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result).split(",")[1];
        setAttachments((p) => [...p, { type: "image", name: f.name, url: URL.createObjectURL(f), base64: b64 }]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const onPickDocs = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (/\.(txt|md|csv|json|log)$/i.test(f.name)) {
        const reader = new FileReader();
        reader.onload = () => setAttachments((p) => [...p, { type: "file", name: f.name, text: String(reader.result).slice(0, 20000) }]);
        reader.readAsText(f);
      } else {
        setAttachments((p) => [...p, { type: "file", name: f.name }]);
      }
    });
    e.target.value = "";
  };

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelRef.current) { cancelRef.current = false; return; }
        const blob = new Blob(chunks, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "audio.webm");
        try {
          const d = await apiFetch("/user/voice/transcribe", { method: "POST", form });
          if (d.text?.trim()) { setText((prev) => (prev ? prev.trimEnd() + " " : "") + d.text.trim()); composerRef.current?.focus(); }
          else toast("Didn't catch that. Try again.");
        } catch { toast("Voice failed. Try again."); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setRecSecs(0);
      clearInterval(recTimerRef.current);
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      toast("Microphone permission is needed for voice.");
    }
  };
  const stopVoice = () => { setRecording(false); clearInterval(recTimerRef.current); mediaRef.current?.stop(); };
  const cancelVoice = () => { cancelRef.current = true; setRecording(false); clearInterval(recTimerRef.current); mediaRef.current?.stop(); };

  const playTTS = async (t) => {
    try {
      const d = await apiFetch("/user/voice/speak", { method: "POST", body: { text: cleanText(t) } });
      if (d.audio_base64) new Audio(`data:audio/mp3;base64,${d.audio_base64}`).play().catch(() => {});
    } catch {}
  };

  const startQuiz = async () => {
    if (quizBusy) return;
    setQuizBusy(true);
    try {
      const res = await apiFetch("/quiz/start", { method: "POST", body: { mode: "solo", topic: quizTopic.trim(), count: quizCount } });
      setQuizModal(false);
      setQuizTopic("");
      setQuizSession(res);
    } catch (e) {
      if (e.status === 402) { navigate("/paywall"); return; }
      toast(e.message || "Couldn't start the quiz.");
    } finally {
      setQuizBusy(false);
    }
  };

  return (
    <div className="relative flex h-screen flex-col bg-background text-foreground">
      <ChatBackground bg={bg} imageUrl={bgImageUrl} />
      {/* Header */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border/70 bg-background/80 px-4 py-3 backdrop-blur-xl">
        <button data-testid="chat-back" onClick={() => navigate("/home")} className="p-1"><ChevronLeft className="h-6 w-6" strokeWidth={1.8} /></button>
        <div className="flex items-center gap-2">
          <span className={cn("flex h-8 w-8 items-center justify-center rounded-full", accent.soft)}>
            <ModeIcon className={cn("h-4 w-4", accent.text)} strokeWidth={2.5} />
          </span>
          <h1 className="font-display text-lg font-bold">{TITLES[tab]}</h1>
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="history-btn" onClick={() => setHistoryOpen(true)}><Clock className="h-5 w-5" strokeWidth={1.5} /></button>
          <button data-testid="new-chat-btn" onClick={() => { setActive(null); setMessages([]); setCurrentChatId(null); }}><Edit3 className="h-5 w-5" strokeWidth={1.5} /></button>
          <div className="relative">
            <button data-testid="chat-more-btn" onClick={() => setBgMenu((o) => !o)}><MoreHorizontal className="h-5 w-5" strokeWidth={1.5} /></button>
            {bgMenu ? (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setBgMenu(false)} />
                <div className="absolute right-0 top-full z-30 mt-1 w-48 rounded-xl border border-border bg-popover p-1 shadow-lg">
                  <button data-testid="chat-background-btn" onClick={() => { setBgMenu(false); setBgPicker(true); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent"><ImageIcon className="h-4 w-4" /> Chat background</button>
                </div>
              </>
            ) : null}
          </div>
          <button data-testid="settings-btn" onClick={() => navigate("/settings")}><SettingsIcon className="h-5 w-5" strokeWidth={1.5} /></button>
        </div>
      </header>

      {/* Sponsor thank-you banner */}
      {modNotice ? (
        <div data-testid="moderation-banner" className="flex items-center gap-3 border-b border-border bg-secondary px-4 py-3">
          <ShieldAlert className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
          <p className="flex-1 text-sm">
            {modNotice.type === "banned" ? "You've been banned from group chats." : modNotice.type === "suspended" ? `You're suspended from group chats until ${(modNotice.until || "").slice(0, 10)}.` : "A moderator sent you a warning about your group-chat behavior."}
            {modNotice.reason ? ` Reason: ${modNotice.reason}.` : ""} You can still chat privately with MAXx.
          </p>
          {modNotice.type === "warned" ? <button data-testid="moderation-dismiss" onClick={dismissModNotice} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button> : null}
        </div>
      ) : null}

      {thankYou ? (
        <div data-testid="thank-you-banner" className="flex items-center gap-3 border-b border-border bg-secondary/60 px-4 py-3">
          <Gift className="h-5 w-5 shrink-0" strokeWidth={1.5} />
          <p className="flex-1 text-sm">
            {thankYou.sponsor_name ? `${thankYou.sponsor_name} covered your MAXx Premium` : "A friend covered your MAXx Premium"} — you can thank them! 🎉
          </p>
          <button data-testid="thank-you-dismiss" onClick={dismissThankYou} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button>
        </div>
      ) : null}

      {/* Messages */}
      <main ref={scrollRef} className="max-scroll relative z-10 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-6">
          {loading ? (
            <div className="flex h-full items-center justify-center py-24"><Spinner className="h-6 w-6 text-primary" /></div>
          ) : messages.length === 0 ? (
            <div className={cn("mx-auto flex max-w-sm flex-col items-center justify-center gap-4 py-24 text-center", hasBg && "my-16 rounded-2xl border border-border/40 bg-background/70 px-6 py-12 backdrop-blur-md")}>
              <span className={cn("flex h-16 w-16 items-center justify-center rounded-2xl", accent.soft)}>
                <ModeIcon className={cn("h-8 w-8", accent.text)} strokeWidth={2.5} />
              </span>
              <h2 className="font-display text-2xl font-bold">{TITLES[tab]}</h2>
              {tab === "learn" ? (
                <button data-testid="learn-quiz-btn" onClick={() => setQuizModal(true)} className="mt-2 inline-flex items-center gap-2 rounded-full border border-learn/40 bg-learn/10 px-5 py-2.5 text-sm font-bold text-learn transition-transform active:scale-95">
                  <Zap className="h-4 w-4" /> 21 Questions
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {messages.map((m) => <div id={`m-${m.id}`} key={m.id}><Bubble m={m} accent={accent} onRetry={() => { setMessages((p) => p.filter((x) => x.id !== m.id)); doSend(m._retry); }} onSpeak={() => playTTS(m.text)} onEdit={startEdit} onCopy={copyMsg} editing={editingId === m.id} editDraft={editDraft} onEditChange={setEditDraft} onEditSave={submitEdit} onEditCancel={cancelEdit} /></div>)}
            </div>
          )}
        </div>
      </main>

      {/* Attachment previews */}
      {attachments.length ? (
        <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-wrap gap-2 px-4 pb-2">
          {attachments.map((a, i) => (
            <div key={i} className="relative">
              {a.type === "image" ? (
                <img src={a.url} alt="" className="h-14 w-14 rounded-lg object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-secondary"><FileText className="h-5 w-5 text-muted-foreground" /></div>
              )}
              <button onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {/* Composer */}
      <div className="relative z-10 border-t border-border bg-background">
        <div className="mx-auto w-full max-w-3xl px-4 py-3">
          <div className={cn("flex gap-1.5 rounded-2xl border bg-secondary px-2 py-1.5 shadow-sm transition-colors", recording ? "items-center border-red-500/40" : "items-end border-border focus-within:border-ring focus-within:ring-1 focus-within:ring-ring")}>
            <input ref={fileImgRef} type="file" accept="image/*" multiple hidden onChange={onPickImages} data-testid="image-input" />
            <input ref={fileDocRef} type="file" multiple hidden onChange={onPickDocs} data-testid="doc-input" />
            {recording ? (
              <VoiceRecordingBar secs={recSecs} onCancel={cancelVoice} onSend={stopVoice} />
            ) : (
              <>
                <div className="relative">
                  <AttachMenu onImages={() => fileImgRef.current?.click()} onDocs={() => fileDocRef.current?.click()} showQuiz={tab === "learn"} onQuiz={() => setQuizModal(true)} />
                </div>
                <textarea
                  ref={composerRef}
                  data-testid="composer-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); } }}
                  placeholder={PLACEHOLDERS[tab] || "Type your question"}
                  rows={1}
                  className="max-h-32 flex-1 resize-none bg-transparent px-1 py-2 outline-none placeholder:text-muted-foreground"
                />
                {text.trim() || attachments.length ? (
                  <button data-testid="send-btn" onClick={() => doSend()} disabled={sending} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <ArrowUp className="h-5 w-5" />
                  </button>
                ) : (
                  <>
                    <button data-testid="voice-mode-btn" onClick={() => setVoiceOpen(true)} title="Talk with MAXx" className="shrink-0 self-center p-1.5 text-muted-foreground hover:text-foreground">
                      <Radio className="h-6 w-6" strokeWidth={1.5} />
                    </button>
                    <button data-testid="voice-btn" onClick={startVoice} className="shrink-0 self-center p-1.5">
                      <Mic className="h-7 w-7" strokeWidth={1.5} />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Bottom nav (only when Research is opened as a primary tab) */}
        {tab === "research" ? <BottomNav active="research" /> : null}
      </div>

      {historyOpen ? <HistoryPanel tab={tab} onClose={() => setHistoryOpen(false)} onOpen={(id) => { setActive(id); setHistoryOpen(false); }} onNew={() => { setActive(null); setMessages([]); setCurrentChatId(null); setHistoryOpen(false); }} /> : null}
      <BackgroundPicker
        open={bgPicker}
        onClose={() => setBgPicker(false)}
        value={bg}
        onPreset={selectPreset}
        onUpload={uploadBg}
        onClear={clearBg}
        uploading={bgUploading}
      />
      <VoiceSession
        open={voiceOpen}
        tab={tab}
        chatId={currentChatId}
        onClose={(fid) => { setVoiceOpen(false); if (fid) { setActive(fid); loadChat(fid); } }}
      />

      {quizModal ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setQuizModal(false)}>
          <div data-testid="quiz-start-modal" className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold"><Zap className="h-5 w-5 text-learn" /> 21 Questions</h2>
              <button onClick={() => setQuizModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">MAXx builds you a quick quiz and you earn XP for every question you play — right or wrong. Pick a topic, or leave it blank for a surprise mix.</p>
            <input
              data-testid="quiz-topic-input"
              value={quizTopic}
              onChange={(e) => setQuizTopic(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") startQuiz(); }}
              placeholder="e.g. fractions, the solar system, WWII"
              className="w-full rounded-xl border border-input bg-background px-4 py-3 text-[15px] text-foreground outline-none placeholder:text-muted-foreground focus:border-foreground"
            />
            <div className="mt-4">
              <div className="mb-2 text-sm font-medium text-muted-foreground">How many questions?</div>
              <div className="flex gap-2">
                {[5, 15, 21].map((n) => (
                  <button key={n} type="button" data-testid={`quiz-size-${n}`} onClick={() => setQuizCount(n)}
                    className={cn("flex-1 rounded-xl border px-3 py-2.5 text-sm font-bold transition-colors", quizCount === n ? "border-learn bg-learn/15 text-learn" : "border-border text-muted-foreground hover:text-foreground")}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="quiz-start-submit" className="mt-4 w-full" loading={quizBusy} onClick={startQuiz}>Start quiz</Button>
          </div>
        </div>
      ) : null}

      {quizSession ? <QuizPlayer session={quizSession} onClose={() => setQuizSession(null)} onDone={() => setQuizSession(null)} /> : null}
    </div>
  );
}

function AttachMenu({ onImages, onDocs, showQuiz, onQuiz }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid="attach-btn" onClick={() => setOpen((o) => !o)} className="p-1.5"><Plus className="h-6 w-6" strokeWidth={1.5} /></button>
      {open ? (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full left-0 z-30 mb-2 w-48 rounded-xl border border-border bg-popover p-1 shadow-lg">
            <button data-testid="attach-photo-library" onClick={() => { setOpen(false); onImages(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Photo Library</button>
            <button data-testid="attach-take-photo" onClick={() => { setOpen(false); onImages(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Take Photo</button>
            <button data-testid="attach-files" onClick={() => { setOpen(false); onDocs(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">Files</button>
            {showQuiz ? (
              <button data-testid="attach-quiz" onClick={() => { setOpen(false); onQuiz(); }} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-learn hover:bg-accent"><Zap className="h-4 w-4" /> 21 Questions</button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Bubble({ m, accent, onRetry, onSpeak, onEdit, onCopy, editing, editDraft, onEditChange, onEditSave, onEditCancel }) {
  const isUser = m.role === "user";
  if (m._typing) {
    return (
      <div className="flex animate-in fade-in slide-in-from-bottom-2 duration-300 ease-out justify-start">
        <div data-testid={m._searching ? "searching-chip" : "typing-indicator"} className="flex items-center gap-2.5 rounded-2xl bg-secondary px-4 py-3">
          {m._searching ? (
            <Search className="h-4 w-4 animate-pulse text-primary" />
          ) : (
            <span className="relative flex h-3.5 w-3.5">
              <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-60", accent?.dot || "bg-primary")} />
              <span className={cn("relative inline-flex h-3.5 w-3.5 rounded-full", accent?.dot || "bg-primary")} />
            </span>
          )}
          <span className="text-sm text-muted-foreground">{m._searching ? "Searching the web…" : "MAXx is thinking…"}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("flex flex-col animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-300 ease-out", isUser ? "items-end" : "items-start")}>
      {(m.attachments || []).length ? (
        <div className={cn("mb-1.5 flex flex-wrap gap-1.5", isUser ? "justify-end" : "justify-start")}>
          {m.attachments.map((a, i) => {
            const src = a.url || (a.path ? `${process.env.REACT_APP_BACKEND_URL}/api/user/creation/file/${a.path}?t=${a.token}` : null);
            return src && a.type === "image"
              ? <img key={i} src={src} alt="" data-testid="message-image" className={cn("rounded-2xl object-cover", isUser ? "h-28 w-28 rounded-xl" : "aspect-square w-full max-w-[280px]")} />
              : <div key={i} className="flex h-28 w-28 items-center justify-center rounded-xl bg-secondary"><FileText className="h-5 w-5 text-muted-foreground" /></div>;
          })}
        </div>
      ) : null}
      {isUser && editing ? (
        <div className="flex w-full max-w-[88%] flex-col gap-2 rounded-2xl border border-primary/50 bg-primary/10 px-3 py-2.5">
          <textarea data-testid="edit-input" autoFocus value={editDraft} onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onEditSave(); } if (e.key === "Escape") onEditCancel(); }}
            rows={2} className="w-full resize-none bg-transparent leading-relaxed text-foreground outline-none" />
          <div className="flex justify-end gap-2">
            <button data-testid="edit-cancel-btn" onClick={onEditCancel} className="rounded-full px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground">Cancel</button>
            <button data-testid="edit-save-btn" onClick={onEditSave} className="rounded-full bg-primary px-4 py-1 text-sm font-semibold text-primary-foreground">Save</button>
          </div>
        </div>
      ) : m.text ? (
        <div data-testid={isUser ? "user-bubble" : "assistant-bubble"} className={cn("max-w-[88%] rounded-2xl px-4 py-3 leading-relaxed", isUser ? "whitespace-pre-wrap bg-primary text-primary-foreground" : "bg-secondary text-foreground")}>
          {isUser ? cleanText(m.text) : <ChatMarkdown text={m.text} />}
        </div>
      ) : null}
      {!isUser && (m.sources || []).length ? (
        <div data-testid="sources-section" className="mt-3 flex max-w-[88%] flex-col gap-1.5">
          <span className="text-xs font-semibold text-muted-foreground">Sources</span>
          {m.sources.map((s, i) => {
            const domain = (s.url || "").replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
            return (
              <a key={i} data-testid={`source-${i}`} href={s.url} target="_blank" rel="noreferrer" className="group flex items-center gap-2.5 rounded-xl border border-border bg-background/40 px-3 py-2 transition-colors hover:bg-secondary">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-[0.65rem] font-semibold text-muted-foreground">{s.n || i + 1}</span>
                <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} alt="" className="h-4 w-4 shrink-0 rounded" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate text-sm font-medium text-foreground group-hover:text-primary">{s.title || domain}</span>
                  <span className="truncate text-xs text-muted-foreground">{domain}</span>
                </span>
              </a>
            );
          })}
        </div>
      ) : null}
      {m._error ? (
        <button data-testid="retry-btn" onClick={onRetry} className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold">
          <AlertTriangle className="h-3.5 w-3.5" /> Not sent, try again.
        </button>
      ) : null}
      {m.text && !m._error && !(isUser && editing) ? (
        <div className={cn("mt-1 flex items-center gap-0.5", isUser ? "justify-end" : "justify-start")}>
          {!isUser ? <button data-testid="speak-btn" onClick={onSpeak} title="Read aloud" className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"><Volume2 className="h-4 w-4" /></button> : null}
          <button data-testid="copy-btn" onClick={() => onCopy(m.text)} title="Copy" className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"><Copy className="h-4 w-4" /></button>
          {isUser ? <button data-testid="edit-btn" onClick={() => onEdit(m)} title="Edit & ask again" className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"><Edit3 className="h-4 w-4" /></button> : null}
        </div>
      ) : null}
    </div>
  );
}

function HistoryPanel({ tab, onClose, onOpen, onNew }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuId, setMenuId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setChats((await apiFetch(`/user/chats?tab=${tab}`)).chats || []); } catch {}
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const togglePin = async (c) => { setMenuId(null); await apiFetch(`/user/chats/${c.id}/${c.pinned ? "unpin" : "pin"}`, { method: "POST" }); load(); };
  const del = async (c) => {
    setMenuId(null);
    setChats((p) => p.filter((x) => x.id !== c.id));
    await apiFetch(`/user/chats/${c.id}`, { method: "DELETE" });
    toast("Chat deleted.", { action: { label: "Undo", onClick: async () => { await apiFetch(`/user/chats/${c.id}/restore`, { method: "POST" }); load(); } } });
  };

  return (
    <div className="fixed inset-0 z-50 bg-background text-foreground">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <button onClick={onClose} className="text-sm font-semibold">Close</button>
        <h1 className="text-lg font-semibold">{TITLES[tab]}</h1>
        <button data-testid="history-new" onClick={onNew}><Edit3 className="h-5 w-5" strokeWidth={1.5} /></button>
      </header>
      <div className="mx-auto max-w-3xl">
        {loading ? (
          <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-foreground" /></div>
        ) : chats.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-muted-foreground">No chats yet in {TITLES[tab]}. Start one when you're ready.</p>
            <Button data-testid="history-empty-start" onClick={onNew}>Start a chat</Button>
          </div>
        ) : (
          chats.map((c) => (
            <div key={c.id} className="flex items-center gap-3 border-b border-border px-4 py-4">
              {c.pinned ? <Bookmark className="h-4 w-4" /> : null}
              <button data-testid={`history-row-${c.id}`} onClick={() => onOpen(c.id)} className="flex-1 truncate text-left">{c.title}</button>
              <div className="relative">
                <button data-testid={`history-menu-${c.id}`} onClick={() => setMenuId(menuId === c.id ? null : c.id)}><MoreHorizontal className="h-5 w-5 text-muted-foreground" /></button>
                {menuId === c.id ? (
                  <div className="absolute right-0 top-full z-30 mt-1 w-40 rounded-xl border border-border bg-popover p-1 shadow-lg">
                    <button data-testid="menu-pin" onClick={() => togglePin(c)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent"><Bookmark className="h-4 w-4" /> {c.pinned ? "Unpin" : "Pin"}</button>
                    <button data-testid="menu-delete" onClick={() => del(c)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent"><Trash2 className="h-4 w-4" /> Delete</button>
                  </div>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
